import { QuestionInfoExt, ClassroomInfoResponse } from '@/services/generatedAPIs.schemas';

/**
 * Represents a saved homework assignment draft
 */
export interface HomeworkDraft {
    id: string;
    contentHash: string; // Hash to identify duplicate drafts
    title: string;
    description?: string;
    topicIds: number[];
    knowledgePointIds?: number[];
    libraryNames: string[];
    subjectId: number;
    subjectName?: string;
    classInfos: ClassroomInfoResponse[];
    dueDate: string; // ISO string for serialization
    questionInfoList?: QuestionInfoExt[];
    language: string;
    shareTeacherIds?: string[];
    savedAt: number; // timestamp
    expiresAt: number; // timestamp
    currentPage: 'aieditresult' | 'manualedit';
}

const HOMEWORK_DRAFT_STORAGE_KEY = 'eeelt_homework_drafts';
const DRAFT_EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Generate a unique ID for a draft based on timestamp
 */
function generateDraftId(): string {
    return `draft_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Generate a content hash for identifying duplicate drafts.
 * Based on: title, topicIds, libraryNames, classInfoIds, dueDate (date part only), and questionIds
 */
function generateContentHash(draft: Omit<HomeworkDraft, 'id' | 'savedAt' | 'expiresAt' | 'contentHash'>): string {
    const hashInput = {
        title: draft.title || '',
        topicIds: [...(draft.topicIds || [])].sort(),
        libraryNames: [...(draft.libraryNames || [])].sort(),
        classInfoIds: (draft.classInfos || []).map(c => c.id).sort(),
        dueDate: draft.dueDate ? draft.dueDate.split('T')[0] : '', // Only date part
        questionIds: (draft.questionInfoList || []).map(q => q.id).filter(Boolean).sort(),
        language: draft.language,
    };
    // Simple hash using JSON string
    const str = JSON.stringify(hashInput);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return `hash_${Math.abs(hash).toString(36)}`;
}

/**
 * Get all non-expired homework drafts from localStorage
 * Auto-cleans expired drafts
 */
export function getHomeworkDrafts(): HomeworkDraft[] {
    try {
        const stored = localStorage.getItem(HOMEWORK_DRAFT_STORAGE_KEY);
        if (!stored) {
            return [];
        }

        const drafts: HomeworkDraft[] = JSON.parse(stored);
        const now = Date.now();

        // Filter out expired drafts
        const validDrafts = drafts.filter(draft => draft.expiresAt > now);

        // If any drafts were cleaned, update localStorage
        if (validDrafts.length !== drafts.length) {
            localStorage.setItem(HOMEWORK_DRAFT_STORAGE_KEY, JSON.stringify(validDrafts));
        }

        return validDrafts;
    } catch (error) {
        console.error('Error reading homework drafts from localStorage:', error);
        return [];
    }
}

/**
 * Save a homework draft to localStorage.
 * If existingDraftId is provided, updates that specific draft.
 * Otherwise, if a draft with the same content hash exists, it will be updated instead of creating a duplicate.
 * Returns the draft ID.
 */
export function saveHomeworkDraft(
    draft: Omit<HomeworkDraft, 'id' | 'savedAt' | 'expiresAt' | 'contentHash'>,
    existingDraftId?: string
): string {
    try {
        const drafts = getHomeworkDrafts();
        const now = Date.now();
        const contentHash = generateContentHash(draft);

        // Priority 1: Update by explicit draft ID if provided
        if (existingDraftId) {
            const idIndex = drafts.findIndex(d => d.id === existingDraftId);
            if (idIndex !== -1) {
                drafts[idIndex] = {
                    ...drafts[idIndex],
                    ...draft,
                    contentHash,
                    savedAt: now,
                    expiresAt: now + DRAFT_EXPIRATION_MS,
                };
                localStorage.setItem(HOMEWORK_DRAFT_STORAGE_KEY, JSON.stringify(drafts));
                return drafts[idIndex].id;
            }
        }

        // Priority 2: Check if a draft with the same content hash already exists (prevent duplicates)
        const hashIndex = drafts.findIndex(d => d.contentHash === contentHash);
        if (hashIndex !== -1) {
            drafts[hashIndex] = {
                ...drafts[hashIndex],
                ...draft,
                contentHash,
                savedAt: now,
                expiresAt: now + DRAFT_EXPIRATION_MS,
            };
            localStorage.setItem(HOMEWORK_DRAFT_STORAGE_KEY, JSON.stringify(drafts));
            return drafts[hashIndex].id;
        }

        // Create new draft
        const newDraft: HomeworkDraft = {
            ...draft,
            id: generateDraftId(),
            contentHash,
            savedAt: now,
            expiresAt: now + DRAFT_EXPIRATION_MS,
        };

        drafts.push(newDraft);
        localStorage.setItem(HOMEWORK_DRAFT_STORAGE_KEY, JSON.stringify(drafts));

        return newDraft.id;
    } catch (error) {
        console.error('Error saving homework draft to localStorage:', error);
        throw error;
    }
}

/**
 * Update an existing homework draft
 */
export function updateHomeworkDraft(
    draftId: string,
    updates: Partial<Omit<HomeworkDraft, 'id' | 'savedAt' | 'expiresAt'>>
): boolean {
    try {
        const drafts = getHomeworkDrafts();
        const index = drafts.findIndex(d => d.id === draftId);

        if (index === -1) {
            return false;
        }

        drafts[index] = {
            ...drafts[index],
            ...updates,
        };

        localStorage.setItem(HOMEWORK_DRAFT_STORAGE_KEY, JSON.stringify(drafts));
        return true;
    } catch (error) {
        console.error('Error updating homework draft:', error);
        return false;
    }
}

/**
 * Remove a homework draft from localStorage
 */
export function removeHomeworkDraft(draftId: string): boolean {
    try {
        const drafts = getHomeworkDrafts();
        const filteredDrafts = drafts.filter(draft => draft.id !== draftId);

        if (filteredDrafts.length === drafts.length) {
            return false; // Draft not found
        }

        localStorage.setItem(HOMEWORK_DRAFT_STORAGE_KEY, JSON.stringify(filteredDrafts));
        return true;
    } catch (error) {
        console.error('Error removing homework draft from localStorage:', error);
        return false;
    }
}

/**
 * Clear all homework drafts from localStorage
 */
export function clearAllHomeworkDrafts(): void {
    try {
        localStorage.removeItem(HOMEWORK_DRAFT_STORAGE_KEY);
    } catch (error) {
        console.error('Error clearing homework drafts:', error);
    }
}
